import { useQuery } from '@tanstack/react-query';
import { Menu, MenuContent, MenuSeparator, MenuTrigger, IconButton, MenuGroup, MenuItem } from '@cad-arc';

import { useApiClient } from '@m43/core';
import { CopyCallMenuItem } from '../active-event-kebab-menu-items/copy-call-menu-item';
import { CopyAndScheduleEventMenuItem } from '../active-event-kebab-menu-items/copy-and-schedule-event-menu-item';
import { AssociateEventMenuItem } from '../active-event-kebab-menu-items/associate-event-menu-item';
import { EditScheduledDateTimeMenuItem } from '../active-event-kebab-menu-items/edit-scheduled-date-time-menu-item';
import { RequestAssistanceMenuItem } from '../active-event-kebab-menu-items/request-assistance-menu-item';
import { ServiceRotationMenuItem } from '../active-event-kebab-menu-items/service-rotation-menu-item';
import { EditDispositionMenuItem } from '../active-event-kebab-menu-items/edit-disposition-menu-item';
import { ManageEventMergeMenuItem } from '../active-event-kebab-menu-items/manage-event-merge-menu-item';
import { observer } from 'mobx-react-lite';
import { useEventMetadataStore } from '../event-panel-form/event-metadata';
import { useEffect } from 'react';
import { ContextMenuResource } from '@mark43/cad-api';

export const ActiveEventKebabMenu = observer(
    ({ eventId }: { eventId?: number }): React.ReactElement => {
        const apiClient = useApiClient();
        const eventPanelMetadata = useEventMetadataStore().state.metadata;
        const isActive = eventPanelMetadata?.isActive;
        const eventNumber = eventPanelMetadata?.agencyEventNumber;

        const { data, refetch } = useQuery(
            ['eventPanelContextMenuView', eventId],
            () => {
                return apiClient
                    .request<ContextMenuResource.GetEventPanelContextMenu>({
                        method: 'GET',
                        url: `context_menu/event_panel/${eventId}/`,
                    })
                    .then((resp) => resp.data.data);
            },
            { enabled: !!eventId }
        );

        useEffect(() => {
            // Any time `isActive` changes, we need to refetch our kebab menu
            refetch();
        }, [isActive, refetch]);

        if (!data) {
            return <></>;
        }

        return (
            <Menu>
                <MenuTrigger asChild>
                    <IconButton
                        data-testid="other-options-menu"
                        variant="ghost"
                        icon="MoreActions"
                        aria-label="more actions"
                    />
                </MenuTrigger>
                <MenuContent>
                    <CopyCallMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data.copyCallContextMenuItemView}
                    ></CopyCallMenuItem>
                    <CopyAndScheduleEventMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.copyAndScheduleEventContextMenuItemView}
                    ></CopyAndScheduleEventMenuItem>
                    <RequestAssistanceMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.requestAssistanceContextMenuItemView}
                    ></RequestAssistanceMenuItem>
                    <ServiceRotationMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.serviceRotationContextMenuItemView}
                    ></ServiceRotationMenuItem>
                    <EditDispositionMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.editDispositionContextMenuItemView}
                    ></EditDispositionMenuItem>
                    <AssociateEventMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.associateEventContextMenuItemView}
                    ></AssociateEventMenuItem>
                    <ManageEventMergeMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.manageEventMergeContextMenuItemView}
                    ></ManageEventMergeMenuItem>
                    <EditScheduledDateTimeMenuItem
                        clickParams={data.clickParams}
                        contextMenuItemView={data?.editScheduledDateTimeContextMenuItemView}
                    ></EditScheduledDateTimeMenuItem>

                    {process.env.NODE_ENV === 'development' && (
                        <>
                            <MenuSeparator />
                            <MenuGroup title="DEV MODE ONLY">
                                <MenuGroup>
                                    <MenuItem
                                        trailingVisual="Duplicate"
                                        onClick={() => navigator.clipboard.writeText(String(eventId))}
                                    >
                                        Event ID: {eventId}
                                    </MenuItem>
                                    <MenuItem
                                        trailingVisual="Duplicate"
                                        onClick={() => navigator.clipboard.writeText(String(eventNumber))}
                                    >
                                        Event Number: {eventNumber}
                                    </MenuItem>
                                </MenuGroup>
                            </MenuGroup>
                        </>
                    )}
                </MenuContent>
            </Menu>
        );
    }
);
